if (!customElements.get('orrenlab-cart-bundle-editor')) {
  class OrrenlabCartBundleEditor extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      try {
        this.details = this.querySelector('[data-cart-bundle-details]');
        this.tierInputs = Array.from(this.querySelectorAll('[data-cart-bundle-tier]'));
        this.panels = Array.from(this.querySelectorAll('[data-cart-bundle-panel]'));
        this.updateButton = this.querySelector('[data-cart-bundle-update]');
        this.cancelButton = this.querySelector('[data-cart-bundle-cancel]');
        this.removeStartButton = this.querySelector('[data-cart-bundle-remove-start]');
        this.removeButton = this.querySelector('[data-cart-bundle-remove]');
        this.removeCancelButton = this.querySelector('[data-cart-bundle-remove-cancel]');
        this.removeConfirm = this.querySelector('[data-cart-bundle-remove-confirm]');
        this.error = this.querySelector('[data-cart-bundle-error]');
        this.liveRegion = this.querySelector('[data-cart-bundle-live]');
        this.status = this.querySelector('[data-cart-bundle-status]');
        this.total = this.querySelector('[data-cart-bundle-total]');
        this.compareTotal = this.querySelector('[data-cart-bundle-compare-total]');
        this.quantityControl = this.querySelector('[data-cart-bundle-quantity-control]');
        this.quantityOutput = this.querySelector('[data-cart-bundle-quantity-output]');
        this.quantityDecrease = this.querySelector('[data-cart-bundle-quantity-decrease]');
        this.quantityIncrease = this.querySelector('[data-cart-bundle-quantity-increase]');
        this.initialTier = Number(this.dataset.currentTier || 1);
        this.currentQuantity = Math.min(10, Math.max(3, this.initialTier));
        this.initialSelections = this.selectedVariantIds(this.initialTier);
        this.isSubmitting = false;

        this.tierInputs.forEach((input) =>
          input.addEventListener('change', (event) => {
            event.stopPropagation();
            input.setCustomValidity('');
            this.selectTier(Number(input.value));
          }),
        );
        this.querySelectorAll('[data-cart-bundle-size]').forEach((input) => {
          input.addEventListener('change', (event) => {
            event.stopPropagation();
            input.setCustomValidity('');
            this.clearError();
            this.updateState(true);
          });
        });
        this.quantityDecrease?.addEventListener('click', () => this.setQuantity(this.selectedTier() - 1));
        this.quantityIncrease?.addEventListener('click', () => this.setQuantity(this.selectedTier() + 1));
        this.updateButton?.addEventListener('click', () => this.updateBundle());
        this.cancelButton?.addEventListener('click', () => this.resetEditor());
        this.removeStartButton?.addEventListener('click', () => this.showRemoveConfirmation());
        this.removeCancelButton?.addEventListener('click', () => this.hideRemoveConfirmation());
        this.removeButton?.addEventListener('click', () => this.removeBundle());

        this.selectTier(this.initialTier, false);
        this.closest('table')?.classList.add('orrenlab-cart-bundle--enhanced');
        this.initialized = true;
      } catch (_error) {
        this.closest('table')?.classList.remove('orrenlab-cart-bundle--enhanced');
      }
    }

    selectedTier() {
      const option = Number(this.tierInputs.find((input) => input.checked)?.value || this.initialTier || 1);
      return option === 3 ? this.currentQuantity : option;
    }

    tierOption(tier) {
      return Number(tier) >= 3 ? 3 : Number(tier);
    }

    panelFor(tier = this.selectedTier()) {
      const option = this.tierOption(tier);
      return this.panels.find((panel) => Number(panel.dataset.cartBundlePanel) === option);
    }

    selectedInputs(tier = this.selectedTier()) {
      return Array.from(
        this.panelFor(tier)?.querySelectorAll('[data-cart-bundle-pair]:not([hidden]) [data-cart-bundle-size]:checked') || [],
      );
    }

    selectedVariantIds(tier = this.selectedTier()) {
      return this.selectedInputs(tier).map((input) => String(input.value));
    }

    selectTier(tier, carrySelections = true) {
      const previousSelections = carrySelections ? this.selectedVariantIds() : this.initialSelections;
      const option = this.tierOption(tier);
      this.currentQuantity = option === 3 ? Math.min(10, Math.max(3, Number(tier) || 3)) : 3;
      this.tierInputs.forEach((input) => {
        input.checked = Number(input.value) === option;
      });
      this.panels.forEach((panel) => {
        panel.hidden = Number(panel.dataset.cartBundlePanel) !== option;
      });

      const panel = this.panelFor(tier);
      Array.from(panel?.querySelectorAll('[data-cart-bundle-pair]') || []).forEach((pair) => {
        pair.hidden = Number(pair.dataset.cartBundlePair) > Number(tier);
      });
      Array.from(panel?.querySelectorAll('[data-cart-bundle-pair]') || []).forEach((pair, index) => {
        if (pair.querySelector('[data-cart-bundle-size]:checked')) return;
        const desiredVariantId = previousSelections[index] || this.initialSelections[index];
        const matchingInput = Array.from(pair.querySelectorAll('[data-cart-bundle-size]')).find(
          (input) => input.value === desiredVariantId && !input.disabled,
        );
        if (matchingInput) matchingInput.checked = true;
      });

      this.updateQuantityControl(option === 3);
      this.clearError();
      this.updateState(true);
    }

    setQuantity(requestedQuantity) {
      if (this.tierOption(this.selectedTier()) !== 3) return;
      const previousSelections = this.selectedVariantIds();
      const quantity = Math.min(10, Math.max(3, Number(requestedQuantity) || 3));
      this.currentQuantity = quantity;
      const panel = this.panelFor(quantity);

      Array.from(panel?.querySelectorAll('[data-cart-bundle-pair]') || []).forEach((pair, index) => {
        pair.hidden = Number(pair.dataset.cartBundlePair) > quantity;
        if (pair.hidden || pair.querySelector('[data-cart-bundle-size]:checked')) return;
        const desiredVariantId = previousSelections[index] || this.initialSelections[index];
        const matchingInput = Array.from(pair.querySelectorAll('[data-cart-bundle-size]')).find(
          (input) => input.value === desiredVariantId && !input.disabled,
        );
        if (matchingInput) matchingInput.checked = true;
      });

      this.updateQuantityControl(true);
      this.clearError();
      this.updateState(true);
    }

    updateQuantityControl(visible) {
      if (this.quantityControl) this.quantityControl.hidden = !visible;
      if (this.quantityOutput) this.quantityOutput.textContent = `${this.currentQuantity} pairs`;
      if (this.quantityDecrease) this.quantityDecrease.disabled = this.currentQuantity <= 3;
      if (this.quantityIncrease) this.quantityIncrease.disabled = this.currentQuantity >= 10;
    }

    discountForQuantity(quantity) {
      // Match the Shopify ORRENLAB-BUNDLE-N total discounts against $49.99 per pair.
      if (quantity === 1) return 999;
      if (quantity === 2) return 3998;
      return quantity >= 3 ? 2499 * quantity : 0;
    }

    updateState(announce = false) {
      const tier = this.selectedTier();
      const inputs = this.selectedInputs(tier);
      const missing = Math.max(0, tier - inputs.length);
      const complete = tier > 0 && missing === 0;
      const compareTotal = inputs.reduce((sum, input) => sum + Number(input.dataset.price || 0), 0);
      const discount = this.discountForQuantity(tier);
      const total = Math.max(0, compareTotal - discount);

      if (this.status) {
        if (complete) this.status.textContent = 'All sizes selected.';
        else if (missing === 1) this.status.textContent = 'Choose a size for 1 more pair.';
        else this.status.textContent = `Choose a size for ${missing} pairs.`;
      }
      if (this.compareTotal) this.compareTotal.textContent = complete ? this.formatMoney(compareTotal) : '';
      if (this.total) this.total.textContent = complete ? this.formatMoney(total) : '';
      if (this.updateButton) this.updateButton.disabled = !complete || this.isSubmitting;
      if (announce) this.announce(this.status?.textContent);
    }

    resetEditor() {
      this.querySelectorAll('[data-cart-bundle-size]').forEach((input) => {
        input.checked = false;
      });
      const initialPanel = this.panelFor(this.initialTier);
      Array.from(initialPanel?.querySelectorAll('[data-cart-bundle-pair]') || []).forEach((pair, index) => {
        const input = Array.from(pair.querySelectorAll('[data-cart-bundle-size]')).find(
          (candidate) => candidate.value === this.initialSelections[index],
        );
        if (input) input.checked = true;
      });
      this.selectTier(this.initialTier, false);
      if (this.details) this.details.open = false;
      this.hideRemoveConfirmation(false);
      this.details?.querySelector('summary')?.focus();
    }

    showRemoveConfirmation() {
      if (!this.removeConfirm || !this.removeStartButton) return;
      this.removeStartButton.hidden = true;
      this.removeConfirm.hidden = false;
      this.removeButton?.focus();
    }

    hideRemoveConfirmation(restoreFocus = true) {
      if (!this.removeConfirm || !this.removeStartButton) return;
      this.removeConfirm.hidden = true;
      this.removeStartButton.hidden = false;
      if (restoreFocus) this.removeStartButton.focus();
    }

    async updateBundle() {
      if (this.isSubmitting) return;
      const tier = this.selectedTier();
      const selectedInputs = this.selectedInputs(tier);
      if (!tier || selectedInputs.length !== tier) {
        this.showError('Choose a size for every pair before updating the bundle.');
        return;
      }

      const newTransactionId = this.transactionId();
      const newItems = selectedInputs.map((input, index) => ({
        id: Number(input.value),
        quantity: 1,
        properties: {
          '_OrrenLab bundle': `${tier}${tier === 1 ? ' Pair' : ' Pairs'}`,
          '_Bundle pair': String(index + 1),
          '_Bundle transaction': newTransactionId,
        },
      }));
      const discount = this.discountForQuantity(tier);
      let beforeCart;
      let originalItems = [];
      let previousCodes = [];
      let newItemsAdded = false;
      let swapAttempted = false;

      this.setLoading(true, 'Updating…');
      this.clearError();

      try {
        beforeCart = await this.getCart();
        const originalLines = this.transactionLines(beforeCart, this.dataset.transactionId);
        if (!originalLines.length) throw new Error('This bundle is no longer in the cart. Refresh the page and try again.');

        originalItems = this.snapshotItems(originalLines);
        previousCodes = this.sanitizedDiscountCodes(this.explicitDiscountCodes(beforeCart));
        const preservedCodes = this.nonBundleCodes(previousCodes);
        const desiredCodes = [...preservedCodes, ...this.bundleCodes(tier)];

        await this.requestJSON('cart/add.js', { items: newItems });
        newItemsAdded = true;

        const stagedCart = await this.getCart();
        if (this.transactionQuantity(stagedCart, newTransactionId) !== tier) {
          throw new Error('The updated bundle could not be staged safely.');
        }
        const stagedOriginalLines = this.transactionLines(stagedCart, this.dataset.transactionId);
        if (!stagedOriginalLines.length) throw new Error('The original bundle could not be located safely.');

        const updates = Object.fromEntries(stagedOriginalLines.map((line) => [line.key, 0]));
        swapAttempted = true;
        const confirmedCart = await this.requestJSON('cart/update.js', {
          updates,
          discount: desiredCodes.join(','),
          sections: this.sectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });
        const validation = this.replacementValidation(confirmedCart, newTransactionId, tier, discount);
        if (!validation.complete) {
          throw new Error('The updated bundle discount could not be confirmed.');
        }
        if (this.transactionLines(confirmedCart, this.dataset.transactionId).length) {
          throw new Error('The original bundle could not be removed safely.');
        }

        document.dispatchEvent(
          new CustomEvent('orrenlab:bundle-updated', {
            detail: { productId: Number(this.dataset.productId), tier, variantIds: newItems.map((item) => item.id) },
          }),
        );
        this.renderCartSections(confirmedCart);
      } catch (error) {
        let recovery = 'original-kept';
        if (newItemsAdded) {
          recovery = await this.restoreOriginalBundle({
            originalTransactionId: this.dataset.transactionId,
            newTransactionId,
            originalItems,
            previousCodes,
            rebuildOriginal: swapAttempted,
          });
        }
        this.setLoading(false);
        if (recovery === 'restored' || recovery === 'original-kept') {
          this.showError(`${error.message || 'The update was not completed.'} Your original bundle has been kept. Please try again.`);
        } else {
          this.showError(error.message || 'The cart could not be confirmed. Refresh the page before trying again.');
        }
      }
    }

    async removeBundle() {
      if (this.isSubmitting) return;
      let originalItems = [];
      let previousCodes = [];
      let removalAttempted = false;

      this.setLoading(true, 'Removing…');
      this.clearError();

      try {
        const beforeCart = await this.getCart();
        const originalLines = this.transactionLines(beforeCart, this.dataset.transactionId);
        if (!originalLines.length) {
          this.renderFreshCart();
          return;
        }
        originalItems = this.snapshotItems(originalLines);
        previousCodes = this.sanitizedDiscountCodes(this.explicitDiscountCodes(beforeCart));
        const updates = Object.fromEntries(originalLines.map((line) => [line.key, 0]));
        removalAttempted = true;
        const confirmedCart = await this.requestJSON('cart/update.js', {
          updates,
          discount: this.nonBundleCodes(previousCodes).join(','),
          sections: this.sectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });
        if (this.transactionLines(confirmedCart, this.dataset.transactionId).length) {
          throw new Error('The complete bundle could not be removed.');
        }

        document.dispatchEvent(
          new CustomEvent('orrenlab:bundle-removed', { detail: { productId: Number(this.dataset.productId) } }),
        );
        this.renderCartSections(confirmedCart);
      } catch (error) {
        let recovery = 'original-kept';
        if (removalAttempted) {
          recovery = await this.restoreOriginalBundle({
            originalTransactionId: this.dataset.transactionId,
            newTransactionId: null,
            originalItems,
            previousCodes,
            rebuildOriginal: true,
          });
        }
        this.setLoading(false);
        this.hideRemoveConfirmation(false);
        if (recovery === 'restored' || recovery === 'original-kept') {
          this.showError('The bundle was not removed. Your original bundle has been kept. Please try again.');
        } else {
          this.showError(error.message || 'The cart could not be confirmed. Refresh the page before trying again.');
        }
      }
    }

    async restoreOriginalBundle({ originalTransactionId, newTransactionId, originalItems, previousCodes, rebuildOriginal }) {
      try {
        previousCodes = this.sanitizedDiscountCodes(previousCodes);
        let cart = await this.getCart();
        const cleanupLines = (cart.items || []).filter((item) => {
          const transactionId = item.properties?.['_Bundle transaction'];
          return transactionId === newTransactionId || (rebuildOriginal && transactionId === originalTransactionId);
        });
        if (cleanupLines.length) {
          await this.requestJSON('cart/update.js', {
            updates: Object.fromEntries(cleanupLines.map((line) => [line.key, 0])),
            discount: this.nonBundleCodes(previousCodes).join(','),
          });
        }
        if (rebuildOriginal && originalItems.length) await this.requestJSON('cart/add.js', { items: originalItems });
        await this.requestJSON('cart/update.js', { discount: previousCodes.join(',') });
        cart = await this.getCart();
        const originalQuantity = originalItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        const restored = this.transactionQuantity(cart, originalTransactionId) === originalQuantity;
        const newRemoved = !newTransactionId || this.transactionQuantity(cart, newTransactionId) === 0;
        return restored && newRemoved ? 'restored' : 'needs-review';
      } catch (_error) {
        return 'needs-review';
      }
    }

    replacementValidation(cart, transactionId, tier, expectedDiscount) {
      const lines = this.transactionLines(cart, transactionId);
      const lineDiscount = lines.reduce(
        (sum, line) => sum + Math.max(0, Number(line.original_line_price || 0) - Number(line.final_line_price || 0)),
        0,
      );
      const appliedCodes = this.explicitDiscountCodes(cart).map((code) => code.toUpperCase());
      const quantity = this.transactionQuantity(cart, transactionId);
      return {
        appliedCodes,
        complete:
          quantity === tier &&
          lines.length === tier &&
          appliedCodes.includes(`ORRENLAB-BUNDLE-${tier}`) &&
          lineDiscount >= Math.max(0, expectedDiscount - 1),
        lineCount: lines.length,
        lineDiscount,
        quantity,
      };
    }

    snapshotItems(lines) {
      return lines.map((line) => {
        const item = { id: Number(line.variant_id || line.id), quantity: Number(line.quantity || 1), properties: line.properties || {} };
        if (line.selling_plan_allocation?.selling_plan?.id) item.selling_plan = line.selling_plan_allocation.selling_plan.id;
        return item;
      });
    }

    transactionLines(cart, transactionId) {
      return (cart?.items || []).filter((item) => item.properties?.['_Bundle transaction'] === transactionId);
    }

    transactionQuantity(cart, transactionId) {
      return this.transactionLines(cart, transactionId).reduce((sum, line) => sum + Number(line.quantity || 0), 0);
    }

    explicitDiscountCodes(cart) {
      const codes = new Set();
      (cart?.cart_level_discount_applications || []).forEach((application) => {
        if (application.type === 'discount_code' && application.title) codes.add(application.title);
      });
      (cart?.items || []).forEach((item) => {
        (item.discount_allocations || item.line_level_discount_allocations || []).forEach((allocation) => {
          const application = allocation.discount_application || allocation;
          if (application.type === 'discount_code' && application.title) codes.add(application.title);
        });
      });
      return Array.from(codes);
    }

    nonBundleCodes(codes) {
      return codes.filter((code) => !String(code).toUpperCase().startsWith('ORRENLAB-BUNDLE-'));
    }

    bundleCodes(tier) {
      return [`ORRENLAB-BUNDLE-${tier}`];
    }

    sanitizedDiscountCodes(codes) {
      return codes.filter((code) => String(code).toUpperCase() !== 'ORRENLAB-BUNDLE-SHIP');
    }

    sectionsToRender() {
      const cartItems = document.getElementById('main-cart-items');
      const cartFooter = document.getElementById('main-cart-footer');
      return [
        { id: 'main-cart-items', section: cartItems?.dataset.id, selector: '.js-contents' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
        { id: 'main-cart-footer', section: cartFooter?.dataset.id, selector: '.js-contents' },
      ].filter((section) => section.section);
    }

    renderCartSections(cart) {
      if (!cart?.sections) {
        this.renderFreshCart();
        return;
      }

      const parser = new DOMParser();
      this.sectionsToRender().forEach((section) => {
        const html = cart.sections[section.section];
        const container = document.getElementById(section.id);
        if (!html || !container) return;

        const sourceDocument = parser.parseFromString(html, 'text/html');
        const source = sourceDocument.querySelector(section.selector);
        const target = container.querySelector(section.selector) || container;
        if (source && target) target.innerHTML = source.innerHTML;
      });

      const isEmpty = Number(cart.item_count || 0) === 0;
      document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
    }

    async renderFreshCart() {
      try {
        const response = await fetch(`${this.shopRoot()}cart?section_id=main-cart-items`, {
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error('The cart could not be refreshed.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const current = document.querySelector('cart-items');
        const fresh = html.querySelector('cart-items');
        if (current && fresh) current.replaceWith(fresh);
      } catch (_error) {
        window.location.reload();
      }
    }

    async requestJSON(path, body) {
      const response = await fetch(`${this.shopRoot()}${path}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || result.message || 'The cart could not be updated.');
      return result;
    }

    async getCart() {
      const response = await fetch(`${this.shopRoot()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('The cart could not be checked.');
      return response.json();
    }

    shopRoot() {
      return window.Shopify?.routes?.root || '/';
    }

    transactionId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `bundle-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
      } catch (_error) {
        return `${(cents / 100).toFixed(2)} ${currency}`;
      }
    }

    setLoading(isLoading, label = 'Updating…') {
      this.isSubmitting = isLoading;
      this.querySelectorAll('button, input').forEach((control) => {
        if (isLoading) {
          control.dataset.wasDisabled = String(control.disabled);
          control.disabled = true;
        } else {
          control.disabled = control.dataset.wasDisabled === 'true';
          delete control.dataset.wasDisabled;
        }
      });
      if (this.updateButton) {
        this.updateButton.setAttribute('aria-busy', String(isLoading));
        this.updateButton.textContent = isLoading ? label : 'Update bundle';
      }
      this.updateState(false);
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
      this.error.focus({ preventScroll: true });
      this.error.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.announce(message);
    }

    announce(message) {
      if (!this.liveRegion || !message) return;
      this.liveRegion.textContent = '';
      window.setTimeout(() => {
        this.liveRegion.textContent = message;
      }, 30);
    }
  }

  customElements.define('orrenlab-cart-bundle-editor', OrrenlabCartBundleEditor);
}
